import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ContentApi } from "../../shared/api/contentApi";

const ContentContext = createContext(undefined);

const LS_PROJECT = "content_projectId";
const LS_VERSION = "content_versionId";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function sanitizeContentId(value) {
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    return UUID_RE.test(normalized) ? normalized : null;
}

function normalizeVersionStatus(status) {
    return typeof status === "string" ? status.trim().toLowerCase() : "";
}

export function ContentProvider({ children }) {
    const [projectId, _setProjectId] = useState(() => sanitizeContentId(localStorage.getItem(LS_PROJECT)));
    const [versionId, _setVersionId] = useState(() => sanitizeContentId(localStorage.getItem(LS_VERSION)));
    const [projects, setProjects] = useState([]);
    const [versions, setVersions] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [forkingVersion, setForkingVersion] = useState(false);

    const selectedVersion = versions.find((v) => v.id === versionId) ?? null;
    const isDraftVersion = normalizeVersionStatus(selectedVersion?.status) === "draft";
    const canMutate = Boolean(projectId && versionId && isDraftVersion);
    const canFork = Boolean(projectId && versionId && !isDraftVersion);

    const refreshVersions = useCallback(async (projectIdArg, preferredVersionId) => {
        const targetProjectId = sanitizeContentId(projectIdArg) ?? projectId;
        if (!targetProjectId) {
            setVersions([]);
            return [];
        }

        const data = await ContentApi.getVersions({ projectId: targetProjectId, pageSize: 100 });
        const list = data?.items ?? [];
        setVersions(list);

        const nextVersion =
            (preferredVersionId && list.find((v) => v.id === preferredVersionId)) ||
            list.find((v) => v.id === versionId) ||
            list.find((v) => normalizeVersionStatus(v.status) === "draft") ||
            list[0] ||
            null;

        const nextVersionId = nextVersion?.id ?? null;
        _setVersionId(nextVersionId);
        if (nextVersionId) {
            localStorage.setItem(LS_VERSION, nextVersionId);
        } else {
            localStorage.removeItem(LS_VERSION);
        }

        return list;
    }, [projectId, versionId]);

    const forkCurrentVersion = useCallback(async () => {
        if (!projectId || !versionId || isDraftVersion || forkingVersion) return null;

        setForkingVersion(true);
        try {
            const created = await ContentApi.forkVersion(projectId, versionId);
            const createdId = created?.id ?? null;
            await refreshVersions(projectId, createdId);
            return created;
        } finally {
            setForkingVersion(false);
        }
    }, [projectId, versionId, isDraftVersion, forkingVersion, refreshVersions]);

    // Load projects once on mount
    useEffect(() => {
        ContentApi.getProjects({ pageSize: 100 })
            .then(data => {
                const list = data?.items ?? [];
                setProjects(list);
                const validStoredProject = list.find((item) => item.id === projectId);
                if (!validStoredProject) {
                    const nextProjectId = list[0]?.id ?? null;
                    _setProjectId(nextProjectId);
                    if (nextProjectId) {
                        localStorage.setItem(LS_PROJECT, nextProjectId);
                    } else {
                        localStorage.removeItem(LS_PROJECT);
                    }
                    _setVersionId(null);
                    localStorage.removeItem(LS_VERSION);
                }
            })
            .catch(() => {})
            .finally(() => setLoadingProjects(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load versions whenever projectId changes
    useEffect(() => {
        if (!projectId) { setVersions([]); return; }
        setLoadingVersions(true);
        ContentApi.getVersions({ projectId, pageSize: 10 })
            .then(data => {
                const list = data?.items ?? [];
                setVersions(list);
                // Keep stored version only if it belongs to this project
                const stillValid = list.find(v => v.id === versionId);
                if (!stillValid) {
                    const draft = list.find(v => normalizeVersionStatus(v.status) === "draft") ?? list[0] ?? null;
                    const nextVersionId = draft?.id ?? null;
                    _setVersionId(nextVersionId);
                    if (nextVersionId) {
                        localStorage.setItem(LS_VERSION, nextVersionId);
                    } else {
                        localStorage.removeItem(LS_VERSION);
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoadingVersions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const setProjectId = useCallback((id) => {
        const nextId = sanitizeContentId(id);
        _setProjectId(nextId);
        _setVersionId(null);
        if (nextId) {
            localStorage.setItem(LS_PROJECT, nextId);
        } else {
            localStorage.removeItem(LS_PROJECT);
        }
        localStorage.removeItem(LS_VERSION);
    }, []);

    const setVersionId = useCallback((id) => {
        const nextId = sanitizeContentId(id);
        _setVersionId(nextId);
        if (nextId) {
            localStorage.setItem(LS_VERSION, nextId);
        } else {
            localStorage.removeItem(LS_VERSION);
        }
    }, []);

    return (
        <ContentContext.Provider value={{
            projectId,
            versionId,
            selectedVersion,
            isDraftVersion,
            canMutate,
            canFork,
            forkingVersion,
            projects,
            versions,
            loadingProjects,
            loadingVersions,
            setProjectId,
            setVersionId,
            refreshVersions,
            forkCurrentVersion,
        }}>
            {children}
        </ContentContext.Provider>
    );
}

export function useContentContext() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error("useContentContext must be used inside <ContentProvider>");
    return ctx;
}
